import { useMemo } from 'react';
import { useFeatureFlag, FEATURE_FLAGS } from '../../../lib/featureFlags';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import { resolveRunMode } from '../../../types/prioritization/session';
import { isRankingSession, isSessionCompleted } from '../utils/sessionHelpers';

export type ParticipantPhase = 'waiting' | 'lobby' | 'brief' | 'room' | 'results';

export interface SessionPhaseState {
  /** Recommended initial phase when entering a session */
  initialPhase: ParticipantPhase;
  isLiveRun: boolean;
  isAsyncRun: boolean;
  isCompleted: boolean;
  isDraft: boolean;
  waitingRoomEnabled: boolean;
}

export function useSessionPhase(session: PrioritizationSession | null | undefined): SessionPhaseState {
  const waitingRoomEnabled = useFeatureFlag(FEATURE_FLAGS.SESSION_WAITING_ROOM);

  return useMemo(() => {
    if (!session) {
      return {
        initialPhase: 'brief',
        isLiveRun: false,
        isAsyncRun: true,
        isCompleted: false,
        isDraft: true,
        waitingRoomEnabled,
      };
    }

    const runMode = resolveRunMode(session.modeConfig);
    const isLiveRun = runMode !== 'open';
    const isAsyncRun = runMode === 'open';
    const isCompleted = isSessionCompleted(session);
    const isDraft = session.state === 'DRAFT';
    // Facilitator-entry sessions: the person arriving IS the facilitator/designer.
    // Never show them the waiting room — they're the one who would open it.
    const isFacilitatorEntry = (session.modeConfig.entryMode ?? 'facilitator') === 'facilitator';

    let initialPhase: ParticipantPhase = 'brief';
    if (isCompleted) {
      initialPhase = 'results';
    } else if (waitingRoomEnabled && !isCompleted && !isFacilitatorEntry && isRankingSession(session)) {
      initialPhase = 'waiting';
    } else if (isLiveRun && !isFacilitatorEntry && isRankingSession(session)) {
      initialPhase = 'lobby';
    } else {
      initialPhase = 'brief';
    }

    return {
      initialPhase,
      isLiveRun,
      isAsyncRun,
      isCompleted,
      isDraft,
      waitingRoomEnabled,
    };
  }, [session, waitingRoomEnabled]);
}
